<template>
  <div>
    <div class="space-y-5">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 class="text-xl font-semibold text-slate-900">Utilization report</h1>
          <p class="mt-1 text-xs text-slate-500">Blood Units transfused to patients at the facility</p>
        </div>
         <div class="flex items-center gap-2">
            <input
              type="date"
              v-model="startDate"
              class="px-3 py-1.5 text-sm border rounded-md border-[#e5e7eb] focus:outline-none focus:ring-2 focus:ring-custom-yellow"
              placeholder="Start date"
              @change="fetchUnits"
            />
            <input
              type="date"
              v-model="endDate"
              class="px-3 py-1.5 text-sm border rounded-md border-[#e5e7eb] focus:outline-none focus:ring-2 focus:ring-custom-yellow"
              placeholder="End date"
              @change="fetchUnits"
            />
            <select
              v-model="filterSituation"
              class="px-3 py-1.5 text-sm border rounded-md border-[#e5e7eb] focus:outline-none focus:ring-2 focus:ring-custom-yellow"
              placeholder="Situation"
              @change="fetchUnits"
            >
              <option value="" disabled>Select situation</option>
              <option v-for="option in situationOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
            <select
              v-model="filterIntent"
              class="px-3 py-1.5 text-sm border rounded-md border-[#e5e7eb] focus:outline-none focus:ring-2 focus:ring-custom-yellow"
              placeholder="Intent"
              @change="fetchUnits"
            >
              <option value="" disabled>Select intent</option>
              <option v-for="option in intentOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
<select
               v-model="filterWard"
               class="px-3 py-1.5 text-sm border rounded-md border-[#e5e7eb] focus:outline-none focus:ring-2 focus:ring-custom-yellow"
               placeholder="Ward/Department"
               @change="fetchUnits"
             >
               <option value="" disabled>Select ward</option>
               <option v-for="dept in departments" :key="dept.department_id" :value="dept.name">
                 {{ dept.name }}
               </option>
             </select>
<button
              type="button"
              class="px-4 py-1.5 text-sm bg-custom-yellow text-white rounded-md hover:bg-yellow-600 disabled:opacity-50"
              :disabled="loading"
              @click="fetchUnits"
            >
             <span v-if="loading" class="flex items-center gap-2">
               <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
               Refreshing…
             </span>
             <span v-else>Refresh</span>
           </button>
           <button
             type="button"
             class="px-4 py-1.5 text-sm bg-rose-100 text-rose-400 border border-rose-200 rounded-md hover:bg-gray-100 disabled:opacity-50"
             @click="clearFilters"
           >
             Clear
           </button>
        </div>
      </header>
        <div
          v-if="error"
          class="px-4 py-2 text-xs border rounded-lg border-amber-200 bg-amber-50 text-amber-700"
        >
          Couldn't load the utilized units — refresh to try again.
        </div>

         <!-- Row 1: units by blood group + product mix -->
         <div class="grid gap-4 p-4 rounded-xl lg:grid-cols-3 bg-[#f1f4f9]">
           <!-- Stacked bar chart -->
           <section class="relative p-5 bg-white rounded-xl lg:col-span-2">
             <header class="flex items-center justify-between mb-4">
               <div>
                 <h2 class="text-sm font-semibold text-slate-900">Units by blood group and product</h2>
                 <p class="mt-0.5 text-[11px] text-[#6b7fa8]">Unit blood group, split by the component transfused</p>
               </div>
               <div v-if="loading" class="flex items-center justify-center w-6 h-6">
                 <svg class="w-5 h-5 animate-spin text-custom-yellow" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
               </div>
             </header>
             <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
               <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
             </div>

            <div class="flex">
              <!-- Y axis -->
              <div class="relative w-8 h-[220px] shrink-0">
                <span
                  v-for="tick in yTicks"
                  :key="tick"
                  class="absolute right-2 text-[10px] text-slate-500 translate-y-1/2"
                  :style="{ bottom: (tick / chartMax) * 100 + '%' }"
                >
                  {{ tick }}
                </span>
              </div>

              <!-- Plot -->
              <div class="flex-1 min-w-0">
                <div class="relative h-[220px]">
                  <div
                    v-for="tick in yTicks"
                    :key="'grid-' + tick"
                    class="absolute left-0 right-0 border-t border-[#eef1f5]"
                    :style="{ bottom: (tick / chartMax) * 100 + '%' }"
                  ></div>

                  <div class="relative flex h-full">
                    <div
                      v-for="group in chartGroups"
                      :key="group.name"
                      class="flex flex-col-reverse items-center flex-1 h-full"
                    >
                      <div
                        v-for="seg in group.segments"
                        :key="seg.code"
                        class="flex-none w-[44%] max-w-[22px]"
                        :style="{
                          height: (seg.count / chartMax) * 100 + '%',
                          backgroundColor: seg.color,
                        }"
                        :title="`${group.label} · ${seg.label}: ${seg.count}`"
                      ></div>
                    </div>
                  </div>
                </div>

                <div class="flex pt-2 border-t border-[#e5e7eb]">
                  <span
                    v-for="group in chartGroups"
                    :key="'label-' + group.name"
                    class="flex-1 text-[11px] text-center text-slate-600"
                  >
                    {{ group.label }}
                  </span>
                </div>
              </div>
            </div>

            <ul class="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-[10px] text-slate-600">
              <li v-for="p in products" :key="p.code" class="flex items-center gap-1.5">
                <span class="w-2 h-2 rounded-sm" :style="{ backgroundColor: p.color }"></span>
                {{ p.label }}
              </li>
            </ul>
          </section>

           <!-- Product mix donut -->
           <section class="relative p-5 bg-white rounded-xl">
             <header class="flex items-center justify-between mb-3">
               <div>
                 <h2 class="text-sm font-semibold text-slate-900">Product mix</h2>
                 <p class="mt-0.5 text-[11px] text-[#6b7fa8]">Share of all utilized units</p>
               </div>
               <div v-if="loading" class="flex items-center justify-center w-6 h-6">
                 <svg class="w-5 h-5 animate-spin text-custom-yellow" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
               </div>
             </header>
             <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
               <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
             </div>

            <div class="flex justify-center">
              <svg viewBox="0 0 160 160" class="w-[170px] h-[170px]">
                <circle cx="80" cy="80" r="60" fill="none" stroke="#eef1f5" stroke-width="22" />
                <circle
                  v-for="seg in donutSegments"
                  :key="seg.code"
                  cx="80"
                  cy="80"
                  r="60"
                  fill="none"
                  :stroke="seg.color"
                  stroke-width="22"
                  :stroke-dasharray="seg.dash"
                  :stroke-dashoffset="seg.offset"
                  transform="rotate(-90 80 80)"
                />
                <text x="80" y="82" text-anchor="middle" font-size="26" font-weight="700" fill="#0f172a">
                  {{ totalUnits }}
                </text>
                <text x="80" y="98" text-anchor="middle" font-size="10" fill="#64748b">units</text>
              </svg>
            </div>

            <ul class="mt-4 space-y-2">
              <li
                v-for="p in productMix"
                :key="p.code"
                class="flex items-center justify-between text-xs"
              >
                <span class="flex items-center gap-2 text-slate-700">
                  <span class="w-2 h-2 rounded-full" :style="{ backgroundColor: p.color }"></span>
                  {{ p.label }}
                </span>
                <span class="flex items-center gap-3">
                  <span class="font-semibold text-slate-900">{{ p.count }}</span>
                  <span class="w-8 text-right text-slate-500">{{ p.percent }}%</span>
                </span>
              </li>
            </ul>
          </section>
        </div>

        <!-- Row 2: recipients + group-specific -->
        <div class="grid gap-4 p-4 rounded-xl lg:grid-cols-3 bg-[#f1f4f9]">
           <!-- Recipients by gender and age -->
           <section class="relative p-5 bg-white rounded-xl lg:col-span-2">
             <header class="flex items-center justify-between mb-4">
               <div>
                 <h2 class="text-sm font-semibold text-slate-900">Recipients by gender and age</h2>
                 <p class="mt-0.5 text-[11px] text-[#6b7fa8]">Number of units transfused</p>
               </div>
               <div v-if="loading" class="flex items-center justify-center w-6 h-6">
                 <svg class="w-5 h-5 animate-spin text-custom-yellow" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
               </div>
             </header>
             <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
               <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
             </div>

            <div class="flex items-center justify-between mb-1.5 text-[11px]">
              <span>
                <span class="font-semibold text-slate-900">Male</span>
                <span class="text-slate-500"><span class="text-[#0f2a6b] px-4">{{ maleCount }}</span>{{ malePct }}%</span>
              </span>
              <span>
                <span class="text-slate-500">{{ femalePct }}%<span class="text-[#f7941d] px-4">{{ femaleCount }}</span></span>
                <span class="font-semibold text-slate-900">Female</span>
              </span>
            </div>
            <div class="flex w-full h-2 overflow-hidden rounded-full bg-slate-100">
              <div class="h-full bg-[#0f2a6b]" :style="{ width: malePct + '%' }"></div>
              <div class="h-full bg-[#f7941d]" :style="{ width: femalePct + '%' }"></div>
            </div>

            <div class="mt-5">
              <div class="flex items-center gap-3 pb-2 text-[10px] text-slate-500 border-b border-[#eef1f5]">
                <span class="w-[72px]">Age group</span>
                <span class="flex-1"></span>
                <span class="w-8 text-right">Male</span>
                <span class="w-8 text-right">Female</span>
                <span class="w-8 text-right">Total</span>
              </div>

              <div
                v-for="row in ageRows"
                :key="row.label"
                class="flex items-center gap-3 py-2.5 border-b border-[#f3f5f8] last:border-0"
              >
                <span class="w-[72px] text-xs text-slate-700">{{ row.label }}</span>
                <div class="flex flex-1 h-2 overflow-hidden rounded-full bg-[#eef1f5]">
                  <div class="h-full bg-[#0f2a6b]" :style="{ width: (row.male / ageMax) * 100 + '%' }"></div>
                  <div class="h-full bg-[#f7941d]" :style="{ width: (row.female / ageMax) * 100 + '%' }"></div>
                </div>
                <span class="w-8 text-xs text-right text-[#0f2a6b]">{{ row.male }}</span>
                <span class="w-8 text-xs text-right text-[#f7941d]">{{ row.female }}</span>
                <span class="w-8 text-xs font-semibold text-right text-slate-900">{{ row.total }}</span>
              </div>
            </div>
          </section>

           <!-- Group-specific gauge -->
           <section class="relative p-5 bg-white rounded-xl">
             <header class="flex items-center justify-between mb-2">
               <div>
                 <h2 class="text-sm font-semibold text-slate-900">Group-specific transfusions</h2>
                 <p class="mt-0.5 text-[11px] text-[#6b7fa8]">Units matching the patient's own blood group</p>
               </div>
               <div v-if="loading" class="flex items-center justify-center w-6 h-6">
                 <svg class="w-5 h-5 animate-spin text-custom-yellow" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" /><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
               </div>
             </header>
             <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
               <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
             </div>

            <div class="flex justify-center">
              <svg viewBox="0 0 160 132" class="w-[190px] h-[157px]">
                <circle
                  cx="80"
                  cy="80"
                  r="60"
                  fill="none"
                  stroke="#eef1f5"
                  stroke-width="16"
                  stroke-linecap="round"
                  :stroke-dasharray="gaugeTrack"
                  transform="rotate(135 80 80)"
                />
                <circle
                  v-if="sameShare > 0"
                  cx="80"
                  cy="80"
                  r="60"
                  fill="none"
                  stroke="#1fa34a"
                  stroke-width="16"
                  stroke-linecap="round"
                  :stroke-dasharray="gaugeValue"
                  transform="rotate(135 80 80)"
                />
                <text x="80" y="82" text-anchor="middle" font-size="26" font-weight="700" fill="#0f172a">
                  {{ sameShare }}%
                </text>
                <text x="80" y="98" text-anchor="middle" font-size="9" fill="#64748b">same group</text>
              </svg>
            </div>

            <div class="grid grid-cols-3 gap-2 pt-4 text-center border-t border-[#e5e7eb]">
              <button
                type="button"
                @click="selectGroupFilter('same')"
                :class="groupFilterActive === 'same' ? 'ring-2 ring-[#0f2a6b] ring-offset-1' : ''"
                class="rounded-md p-1 -m-1 hover:bg-[#0f2a6b]/5 transition"
              >
                <p class="text-[10px] text-slate-500">Same group</p>
                <p class="mt-1 text-sm font-semibold text-[#0f2a6b]">{{ groupStats.same }}</p>
              </button>
              <button
                type="button"
                @click="selectGroupFilter('substitute')"
                :class="groupFilterActive === 'substitute' ? 'ring-2 ring-[#f7941d] ring-offset-1' : ''"
                class="rounded-md p-1 -m-1 hover:bg-[#f7941d]/5 transition"
              >
                <p class="text-[10px] text-slate-500">Substitutes</p>
                <p class="mt-1 text-sm font-semibold text-[#f7941d]">{{ groupStats.substitutes }}</p>
              </button>
              <button
                type="button"
                @click="selectGroupFilter('universal')"
                :class="groupFilterActive === 'universal' ? 'ring-2 ring-[#0f2a6b] ring-offset-1' : ''"
                class="rounded-md p-1 -m-1 hover:bg-[#0f2a6b]/5 transition"
              >
                <p class="text-[10px] text-slate-500">O− as universal</p>
                <p class="mt-1 text-sm font-semibold text-[#0f2a6b]">{{ groupStats.universal }}</p>
              </button>
            </div>
          </section>
        </div>

         <!-- Utilized units table -->
         <section class="overflow-hidden bg-white border rounded-xl border-[#e5e7eb] relative">
           <header class="flex items-center justify-between px-5 py-4">
             <h2 class="text-sm font-semibold text-slate-900">Utilized units</h2>
             <span class="text-[11px] text-[#6b7fa8]">{{ tableRows.length }} records</span>
           </header>
           <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
             <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
           </div>
           <div class="overflow-x-auto transition-opacity" :class="{ 'opacity-60': loading }">
            <table class="w-full min-w-[760px] text-sm border-collapse">
              <thead>
                 <tr class="text-left text-xs bg-[#f8fafc] border-y border-[#e5e7eb] text-slate-800">
                   <th class="px-5 py-3 font-semibold">Serial Number</th>
                   <th class="px-3 py-3 font-semibold">Blood Group</th>
                   <th class="px-3 py-3 font-semibold">Blood Product</th>
                   <th class="px-3 py-3 font-semibold">Patient Name</th>
                   <th class="px-3 py-3 font-semibold">Gender</th>
                   <th class="px-3 py-3 font-semibold">Age</th>
                   <th class="px-5 py-3 font-semibold">Patient Blood Group</th>
                   <th class="px-3 py-3 font-semibold">Situation</th>
                   <th class="px-3 py-3 font-semibold">Intent</th>
                   <th class="px-3 py-3 font-semibold">Ward/Department</th>
                 </tr>
              </thead>
              <tbody>
                 <tr
                   v-for="row in visibleRows"
                   :key="row.serial"
                   class="border-b border-[#eef1f5] text-xs text-slate-700"
                 >
                   <td class="px-5 py-4">{{ row.serial }}</td>
                   <td class="px-3 py-4">{{ row.unit_group }}</td>
                   <td class="px-3 py-4">{{ row.product }}</td>
                   <td class="px-3 py-4">{{ row.patient_name }}</td>
                   <td class="px-3 py-4">{{ row.gender }}</td>
                   <td class="px-3 py-4">{{ row.age }}</td>
                   <td
                     class="px-5 py-4"
                     :class="row.patient_group !== row.unit_group ? 'text-[#f7941d]' : ''"
                     :title="row.patient_group !== row.unit_group ? 'Different from the unit blood group' : ''"
                   >
                     {{ row.patient_group }}
                   </td>
                   <td class="px-3 py-4">{{ row.situation }}</td>
                   <td class="px-3 py-4">{{ row.intent }}</td>
                   <td class="px-3 py-4">{{ row.ward }}</td>
                 </tr>
                 <tr v-if="!visibleRows.length">
                   <td colspan="10" class="py-10 text-sm text-center text-slate-400">
                     No utilized units found.
                   </td>
                 </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="totalPages > 1"
            class="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-[#e5e7eb] bg-[#f8fafc]"
          >
            <p class="text-[11px] text-slate-500">
              Showing {{ visibleRows.length }} of {{ tableRows.length }} records
            </p>
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="flex items-center justify-center w-7 h-7 bg-white border rounded-md border-[#e5e7eb] text-slate-500 disabled:opacity-40"
                :disabled="currentPage === 1"
                aria-label="Previous page"
                @click="currentPage--"
              >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <span class="text-[11px] text-slate-500">Page {{ currentPage }} of {{ totalPages }}</span>
              <button
                type="button"
                class="flex items-center justify-center w-7 h-7 bg-white border rounded-md border-[#e5e7eb] text-slate-500 disabled:opacity-40"
                :disabled="currentPage === totalPages"
                aria-label="Next page"
                @click="currentPage++"
              >
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6" /></svg>
              </button>
            </div>
          </div>
        </section>
        </div>
      </div>
    </template>

<script>
import api from "@/services/api";

const GROUPS = [
  { name: "A+", short: "A+" },
  { name: "A-", short: "A−" },
  { name: "B+", short: "B+" },
  { name: "B-", short: "B−" },
  { name: "AB+", short: "AB+" },
  { name: "AB-", short: "AB−" },
  { name: "O+", short: "O+" },
  { name: "O-", short: "O−" },
];

const PRODUCTS = [
  { code: "PRC", label: "Packed Red Cells", color: "#0f2a6b" },
  { code: "WB", label: "Whole Blood", color: "#f7941d" },
  { code: "FFP", label: "Fresh Frozen Plasma", color: "#2a9fb0" },
  { code: "PLT", label: "Platelets", color: "#8ea3de" },
  { code: "CRYO", label: "Cryoprecipitate", color: "#d9541e" },
];

const AGE_BUCKETS = [
  { label: "0–17 yrs", min: 0, max: 17 },
  { label: "18–30 yrs", min: 18, max: 30 },
  { label: "31–45 yrs", min: 31, max: 45 },
  { label: "46–60 yrs", min: 46, max: 60 },
  { label: "60+ yrs", min: 61, max: 200 },
];

const DONUT_R = 60;
const DONUT_C = 2 * Math.PI * DONUT_R;
const GAUGE_C = 2 * Math.PI * 60;
const GAUGE_ARC = GAUGE_C * 0.75; // 270° gauge, open at the bottom

export default {
  name: "UtilizedUnitsReportTab",
    data() {
      return {
        loading: false,
        error: null,
        units: [],
        products: PRODUCTS,
        currentPage: 1,
        perPage: 10,
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        filterSituation: '',
        filterIntent: '',
        filterWard: '',
        filterGroup: '',
        groupFilterActive: '',
        departments: [],
        situationOptions: [
          { value: 'normal', label: 'Normal' },
          { value: 'emergency', label: 'Emergency' },
          { value: 'desperate', label: 'Desperate' },
          { value: 'noXmatch', label: 'Desparate (No X-match)' },
          { value: 'Urgent', label: 'Urgent' },
          { value: 'xmatch', label: 'Urgent (X-match)' },
          { value: 'nonurgent', label: 'Non-Urgent' }
        ],
        intentOptions: [
          { value: 'transfusion', label: 'For Transfusion' },
          { value: 'theatre', label: 'For Theatre' }
        ]
      };
    },
     created() {
       this.fetchDepartments();
       this.fetchUnits();
     },
  computed: {
    filteredUnits() {
      if (this.groupFilterActive === 'same') {
        return this.units.filter((u) => u.unit_group === u.patient_group);
      }
      if (this.groupFilterActive === 'universal') {
        return this.units.filter((u) => u.unit_group === 'O-');
      }
      if (this.groupFilterActive === 'substitute') {
        return this.units.filter((u) => u.unit_group !== u.patient_group && u.unit_group !== 'O-');
      }
      return this.units;
    },
    tableRows() {
      return this.filteredUnits;
    },
    totalUnits() {
      return this.units.length;
    },

    /* ------------------------ units by blood group ------------------------ */
    chartGroups() {
      return GROUPS.map((group) => {
        const segments = PRODUCTS.map((p) => ({
          code: p.code,
          label: p.label,
          color: p.color,
          count: this.units.filter(
            (u) => u.unit_group === group.name && u.product === p.code
          ).length,
        }));
        return {
          name: group.name,
          label: group.short,
          segments,
          total: segments.reduce((sum, s) => sum + s.count, 0),
        };
      });
    },
    chartStep() {
      return this.niceStep(Math.max(0, ...this.chartGroups.map((g) => g.total)));
    },
    chartMax() {
      return this.chartStep * 4;
    },
    yTicks() {
      return [0, 1, 2, 3, 4].map((i) => i * this.chartStep);
    },

    /* ------------------------------ product mix --------------------------- */
    productMix() {
      return PRODUCTS.map((p) => {
        const count = this.units.filter((u) => u.product === p.code).length;
        return {
          ...p,
          count,
          percent: this.totalUnits ? Math.round((count / this.totalUnits) * 100) : 0,
        };
      }).sort((a, b) => b.count - a.count);
    },
    donutSegments() {
      if (!this.totalUnits) return [];
      let offset = 0;
      return this.productMix
        .filter((p) => p.count > 0)
        .map((p) => {
           const len = (p.count / this.totalUnits) * DONUT_C;
           const visible = Math.max(len - 2, 0); // small gap between slices
           const seg = {
             code: p.code,
             color: p.color,
             dash: `${visible} ${DONUT_C - visible}`,
             offset: -offset,
           };
           offset += len;
           return seg;
        });
    },

    /* --------------------------- recipients ------------------------------- */
    maleCount() {
      return this.units.filter((u) => u.gender === "Male").length;
    },
    femaleCount() {
      return this.units.filter((u) => u.gender === "Female").length;
    },
    malePct() {
      return this.totalUnits ? Math.round((this.maleCount / this.totalUnits) * 100) : 0;
    },
    femalePct() {
      return this.totalUnits ? 100 - this.malePct : 0;
    },
    ageRows() {
      return AGE_BUCKETS.map((b) => {
        const inBucket = this.units.filter((u) => u.age >= b.min && u.age <= b.max);
        const male = inBucket.filter((u) => u.gender === "Male").length;
        const female = inBucket.filter((u) => u.gender === "Female").length;
        return { label: b.label, male, female, total: male + female };
      });
    },
    ageMax() {
      return Math.max(1, ...this.ageRows.map((r) => r.total));
    },

    /* ----------------------- group-specific gauge ------------------------- */
    groupStats() {
      let same = 0;
      let substitutes = 0;
      let universal = 0;
      this.units.forEach((u) => {
        if (u.unit_group === u.patient_group) same++;
        else if (u.unit_group === "O-") universal++;
        else substitutes++;
      });
      return { same, substitutes, universal };
    },
    sameShare() {
      return this.totalUnits
        ? Math.round((this.groupStats.same / this.totalUnits) * 100)
        : 0;
    },
    gaugeTrack() {
      return `${GAUGE_ARC} ${GAUGE_C}`;
    },
    gaugeValue() {
      return `${GAUGE_ARC * (this.sameShare / 100)} ${GAUGE_C}`;
    },

    /* -------------------------------- table ------------------------------- */
    totalPages() {
      return Math.max(1, Math.ceil(this.tableRows.length / this.perPage));
    },
    visibleRows() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.tableRows.slice(start, start + this.perPage);
    },
  },
  methods: {
    // round max/4 up to a whole-number step so ticks are 0, s, 2s, 3s, 4s
    niceStep(max) {
      if (max <= 0) return 1;
      const rough = max / 4;
      const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
      const normalized = rough / magnitude;
      const nice = [1, 2, 4, 5, 10].find((n) => n >= normalized) || 10;
      return Math.max(1, nice * magnitude);
    },

     async fetchUnits() {
       this.loading = true;
       this.error = null;
       try {
         const response = await api.getUtilizationReport({
           start_date: this.startDate,
           end_date: this.endDate,
           situation: this.filterSituation,
           intent: this.filterIntent,
           ward: this.filterWard
         });
         this.units = response.units || [];
         this.currentPage = 1;
       } catch (error) {
         this.error = error.message || "Failed to fetch utilized units";
         console.error("Error fetching utilized units:", error);
} finally {
          this.loading = false;
        }
      },
      async clearFilters() {
        this.filterSituation = '';
        this.filterIntent = '';
        this.filterWard = '';
        this.groupFilterActive = '';
        await this.fetchUnits();
      },
      selectGroupFilter(key) {
        this.groupFilterActive = this.groupFilterActive === key ? '' : key;
        this.currentPage = 1;
      },
      async fetchDepartments() {
        try {
          const response = await api.getDepartments();
          this.departments = (response.data || response);
        } catch (error) {
          console.error('Error fetching departments:', error);
          this.departments = [];
        }
      },
    },
};
</script>

<style lang="css" scoped></style>